import React, { useState, useRef } from "react";
import { CustomIcon } from "./shared/CustomIcon";

const SWIPE_THRESHOLD = 80;

// =============================================================
// SwipeableNotif — single notification row with horizontal swipe-to-delete
// gesture (touch + pointer) and a desktop × button. Notifies the parent
// via onDelete after a brief 160 ms exit animation when the swipe passes
// the threshold; otherwise snaps back to neutral.
// =============================================================
export default function SwipeableNotif({ notif, onClick, onDelete, isRead, "data-testid": testId }) {
  const [dx, setDx] = useState(0);
  const [snap, setSnap] = useState(true);
  const startX = useRef(0);
  const dragging = useRef(false);

  const begin = (clientX) => { startX.current = clientX; dragging.current = true; setSnap(false); };
  const move = (clientX) => { if (!dragging.current) return; setDx(Math.min(0, clientX - startX.current)); };
  const end = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setSnap(true);
    if (dx < -SWIPE_THRESHOLD) {
      setDx(-600);
      setTimeout(() => onDelete(notif), 160);
    } else {
      setDx(0);
    }
  };

  const handleClick = (e) => {
    if (dx !== 0) { e.preventDefault(); e.stopPropagation(); return; }
    onClick && onClick(notif);
  };

  const swiping = Math.abs(dx) > 6;

  return (
    <div className={`notif-row ${swiping ? "is-swiping" : ""}`} data-testid={testId}>
      <div className="delete-bg" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="trash" size={18} /> Release to delete
      </div>
      <div
        className={`notif-content-swipe ${snap ? "snap" : ""}`}
        style={{ transform: `translateX(${dx}px)` }}
        onTouchStart={(e) => begin(e.touches[0].clientX)}
        onTouchMove={(e) => move(e.touches[0].clientX)}
        onTouchEnd={end}
        onTouchCancel={end}
        onPointerDown={(e) => { if (e.pointerType !== "touch") begin(e.clientX); }}
        onPointerMove={(e) => { if (dragging.current) move(e.clientX); }}
        onPointerUp={end}
        onPointerCancel={end}
      >
        <div
          className={`notif-item ${notif.type === "birthday" ? "notif-birthday" : ""}`}
          role="link" tabIndex={0}
          onClick={handleClick}
          onKeyDown={(e) => e.key === "Enter" && onClick && onClick(notif)}
          style={{ background: isRead ? "" : "color-mix(in srgb, var(--kc-accent) 4%, transparent)" }}
        >
          <div className="notif-title">
            {!isRead && <span style={{ display: "inline-block", width: 6, height: 6, background: "var(--kc-accent)", borderRadius: "50%", marginRight: 6, verticalAlign: "middle" }} />}
            {notif.text}
          </div>
          <div className="notif-time">{notif.time}</div>
        </div>
        <button
          className="desktop-delete-btn"
          data-testid={testId ? `${testId}-x` : undefined}
          onClick={(e) => { e.stopPropagation(); onDelete(notif); }}
          aria-label="Delete notification"
          title="Delete"
        >×</button>
      </div>
    </div>
  );
}
